import React, {useState, useRef, useEffect, useCallback} from "react"
import {useAuth0} from "@auth0/auth0-react"
// Redux
import {useDispatch, useSelector} from "react-redux"
import moment from "moment"
// lodash
import debounce from "lodash/debounce"
// UI
import {Container, Row, Col, ContainerFluid} from "@uprise/grid"
import styled from "styled-components"
import {TextInputHorizontal, TextArea} from "@uprise/form"
import {Alert} from "@uprise/alert"
import {H4, H6, H5} from "@uprise/headings"
import {spacing} from "@uprise/spacing"
import {P, Medium} from "@uprise/text"
import {Accordian} from "@uprise/accordian"
import {Button} from "@uprise/button"
import {useForm, Controller} from "react-hook-form"
import {extended, semantic} from "@uprise/colors"
import {Modal} from "@uprise/modal"
// Slices
import {userDataSelector, updateSessionDetails, updateSessionNotes} from "features/Users/usersSlice"
import {authSelector} from "features/Auth/authSlice"
import {bookingDataSelector} from "features/Bookings/bookingSlice"
// Components
import AlertCustom from "components/shared/AlertCustom"
import {HeaderTableContent, CustomButton} from "../Bookings"
import {ModalButtonContainer} from "../../../Bookings/AllBookingsTable/index.style"
import Activity from "../Activity"
import NotesContainer from "../NotesContainer"
import DropDown from "components/shared/DropDown"
import Radio from "components/shared/Radio"
import WellbeingScore from "../WellbeingScore"
import {SurveyResults} from "../../Results/Exercises/SurveyResults"
// constants
import {issuesDropdown} from "constants/issues"
import {RECOMMENDATIONS} from "constants/recommendation"
import {SESSION_DETAILS} from "constants/sessionDetails"
// helpers
import {calcWellbeingScores} from "helpers/utils"

const TextAreaVert = styled(TextArea)`
	resize: vertical;
	margin-bottom: 0;
`

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementContainerFullWidth = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	//border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	position: relative;
	align-self: center;
`

const FormTextInputWithBorder = styled(TextInputHorizontal)`
	border-radius: 4px;
	border: solid 1px #d4cbfb;
	padding: 4px 10px;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	outline: none;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	margin-bottom: 8px;
	transition: font-size 0.2s;
	//padding-left: 40%;
	text-align: left;
`

const FormInputInlineContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-basis: 60%;
`

const MessageStylesCaps = styled(MessageStyles)`
	padding-left: 40%;
	text-align: left;
`

const ReadOnlyField = styled.div`
	color: #6d6c79;
	text-align: left;
`

const ReadOnlyFieldStyled = styled(ReadOnlyField)`
	color: #6d6c79;
	text-align: left;
	display: flex;
`

const ModalStyled = styled(Modal)`
	img {
		display: none;
	}
`

const AccordianStyled = styled(Accordian)`
	> div > div:first-child {
		background: ${extended.purple.five};
		margin: -15px -15px;
		padding: 15px;
		border-radius: 4px;
		align-items: center;
	}
	> div:last-child {
		border-bottom: unset;
	}
	& h4 {
		width: 100%;
		font-size: 16px;
	}
	& img {
		margin-top: 0;
	}
`

const HR = styled.div`
	width: 100%;
	height: 1px;
	background: ${extended.purple.three};
	margin: 10px 0;
`

const ExpiredBtn = styled(Button)`
	color: black;
	font-size: 12px;
	background: ${extended.charcoal.four};
	font-family: normal;
	font-family: Proxima Nova;
	pointer-events: none;
`

const CompleteSession = ({
	onCancel,
	rcbNotes,
	onComplete,
	booking,
	sessionNote,
	coachingRemaining,
	therapyRemaining,
	codeExpired,
	assignedCoach
}) => {
	const [showModal, setShowModal] = useState(false)
	const [loading, setLoading] = useState(false)
	const [enableRecommend, setEnableRecommend] = useState(false)
	const [enableIssue, setEnableIssue] = useState(false)

	const dispatch = useDispatch()
	const {getAccessTokenSilently} = useAuth0()
	const authData = useSelector(authSelector)

	const {register, control, handleSubmit, errors, setValue, watch, trigger} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onSubmit",
		reValidateMode: "onChange"
	})

	const {results, tasks, profile, fetchSessionDetailsApiState, userNotesApiState} = useSelector(userDataSelector)
	const {fetchCoachesApiState} = useSelector(bookingDataSelector)

	const token = useRef(null)

	const getToken = () => {
		if (!token.current) {
			return new Promise(async (res, rej) => {
				try {
					const authToken = await getAccessTokenSilently()
					token.current = authToken
					res(authToken)
				} catch (err) {
					console.log(err)
					rej(err)
				}
			})
		} else return Promise.resolve()
	}

	const populateForm = () => {
		const {coachTopic, recommendation, completionStatus} = booking
		if (!coachTopic) {
			// do nothing
		} else if (issuesDropdown.includes(coachTopic)) {
			setEnableIssue(false)
			setValue("issues", coachTopic, {
				shouldValidate: true
			})
		} else {
			setEnableIssue(true)
			setValue("issues", issuesDropdown[17], {
				shouldValidate: true
			})
			setValue("otherIssue", coachTopic, {
				shouldValidate: true
			})
		}

		if (!recommendation) {
			// do nothing
		} else if (RECOMMENDATIONS.includes(recommendation)) {
			setEnableRecommend(false)
			setValue("recommendation", recommendation, {
				shouldValidate: true
			})
		} else {
			setEnableRecommend(true)
			setValue("recommendation", RECOMMENDATIONS[6], {
				shouldValidate: true
			})
			setValue("otherRecommendation", recommendation, {
				shouldValidate: true
			})
		}

		if (!completionStatus) {
			setValue("sessionDuration", SESSION_DETAILS[0], {
				shouldValidate: true
			})
			setValue("sessionDetails", "coaching-completed", {
				shouldValidate: true
			})
		} else if (SESSION_DETAILS.includes(completionStatus)) {
			if (completionStatus === SESSION_DETAILS[2] || completionStatus === SESSION_DETAILS[3]) {
				setValue("sessionDuration", completionStatus, {
					shouldValidate: true
				})
				setValue("sessionDetails", "therapy-completed", {
					shouldValidate: true
				})
			} else if (completionStatus === SESSION_DETAILS[0] || completionStatus === SESSION_DETAILS[1]) {
				setValue("sessionDuration", completionStatus, {
					shouldValidate: true
				})
				setValue("sessionDetails", "coaching-completed", {
					shouldValidate: true
				})
			} else {
				setValue("sessionDetails", completionStatus, {
					shouldValidate: true
				})
			}
		}
	}

	const {name, employerCode, coach} = profile

	useEffect(() => {
		populateForm()
	}, [])

	useEffect(() => {
		setEnableRecommend(watch("recommendation") === "Other")
		setEnableIssue(watch("issues") === "Others")
	}, [watch("recommendation"), watch("issues")])

	const validateText = (text, field) => {
		return !text.trim() ? `${field} can not be left empty` : true
	}

	const {start, duration, timezone, coachEmail, id, coachId, coachName} = booking
	const watchSession = watch(["sessionDetails", "sessionDuration"])
	const watchTextArea = watch(["remarks", "content"])

	const handleForm = formData => {
		const {
			remarks,
			content,
			issues,
			sessionDetails,
			otherRecommendation,
			otherIssue,
			recommendation,
			sessionDuration
		} = formData

		let data = {
			bookingId: id,
			recommendation: enableRecommend ? otherRecommendation : recommendation,
			coachTopic: enableIssue ? otherIssue : issues,
			note: content,
			completionStatus: ["coaching-completed", "therapy-completed"].includes(sessionDetails)
				? sessionDuration
				: sessionDetails
		}

		if (remarks) data = {...data, remarks}

		getToken().then(() => {
			setLoading(true)
			dispatch(updateSessionDetails({data, token: token.current}))
				.then(() => {
					onCancel()
				})
				.finally(() => setLoading(false))
		})
	}

	const handleProceed = () => {
		setShowModal(false)
		onCancel()
	}

	const handleModalClose = () => setShowModal(false)

	const {who5, stress} = calcWellbeingScores(results)

	const resultsSurvey = results?.filter(result => {
		return result.taskId.includes("exercise")
	})

	const autoSaveNotesApiCall = useCallback(
		debounce(value => {
			const curr = fetchSessionDetailsApiState.data.filter(note => note.bookingId === id)
			if (curr.length && !errors?.["content"]) {
				getToken().then(() => {
					setLoading(true)
					dispatch(
						updateSessionNotes({
							data: {
								id: curr[0].id,
								note: value
							},
							token: token.current
						})
					)
						.catch(err => console.log("Error auto saving notes", err))
						.finally(() => setLoading(false))
				})
			}
		}, 30 * 1000),
		[]
	)

	return (
		<ContainerFluid>
			<form
				onSubmit={handleSubmit(data => {
					handleForm(data)
				})}
			>
				<Row>
					<HR style={{marginBottom: 0}} />
				</Row>

				{codeExpired && (
					<AlertCustom
						className='m-b-10'
						textAlign='center'
						type={"error"}
						message={"Employer code has expired. Please contact operations for support."}
					/>
				)}

				<div style={{padding: 24}}>
					<Row>
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
							<FormElementContainer>
								<FormElementLabel>Booking Session</FormElementLabel>
								<FormInputContainer>
									<ReadOnlyField>
										{`${moment(start).format("DD MMMM YYYY, hh:mm A")} ${moment()
											.tz(timezone || "Australia/Sydney")
											.format("z")} (${duration} minutes) `}
									</ReadOnlyField>
								</FormInputContainer>
							</FormElementContainer>
						</Col>
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
							<FormElementContainer>
								<FormElementLabel>Coach</FormElementLabel>
								<FormInputContainer>
									<ReadOnlyField>{coachName}</ReadOnlyField>
								</FormInputContainer>
							</FormElementContainer>
						</Col>
					</Row>
					<Row>
						<Col>
							<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
								User Details
							</H4>
						</Col>
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
							<Row>
								<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
									<FormElementContainer>
										<FormElementLabel>Name</FormElementLabel>
										<FormInputContainer>
											<ReadOnlyField>{name}</ReadOnlyField>
										</FormInputContainer>
									</FormElementContainer>
								</Col>
								<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
									<FormElementContainer>
										<FormElementLabel>Employer Code</FormElementLabel>
										<FormInputContainer>
											<ReadOnlyFieldStyled>
												{employerCode}
												{codeExpired && (
													<ExpiredBtn
														className='m-l-4'
														title='expired'
														size='tiny'
														fullWidth={false}
														variant='text'
													/>
												)}
											</ReadOnlyFieldStyled>
										</FormInputContainer>
									</FormElementContainer>
								</Col>
								<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
									<FormElementContainer>
										<FormElementLabel>Coaching Sessions</FormElementLabel>
										<FormInputContainer>
											<ReadOnlyField>{coachingRemaining}</ReadOnlyField>
										</FormInputContainer>
									</FormElementContainer>
								</Col>
								<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
									<FormElementContainer>
										<FormElementLabel>Counselling Sessions</FormElementLabel>
										<FormInputContainer>
											<ReadOnlyField>{therapyRemaining}</ReadOnlyField>
										</FormInputContainer>
									</FormElementContainer>
								</Col>
							</Row>
						</Col>
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12 align-self-center'>
							<Row>
								<WellbeingScore
									heading={"Wellbeing Score"}
									date={moment.unix(who5.timestamp).format("D MMM YYYY")}
									score={who5.score}
									text={"(higher is better)"}
									isStress={false}
								/>
								<WellbeingScore
									heading={"Stress Score"}
									date={moment.unix(stress.timestamp).format("D MMM YYYY")}
									score={stress.score}
									text={"(lower is better)"}
									isStress={true}
								/>
							</Row>
						</Col>
					</Row>

					<Row className='text-left m-t-10 m-b-5'>
						<Col>
							<AccordianStyled
								content={[
									{
										header: (
											<Row className='align-center'>
												<Col className='col-12'>Modules Completed</Col>
											</Row>
										),
										content: (
											<div className='m-t-5'>
												{resultsSurvey.length ? (
													<SurveyResults
														className='m-b-5'
														results={resultsSurvey}
														tasks={tasks}
														custom={true}
													/>
												) : (
													<Row>
														<Col>
															<H4 className='text-center'>No Data Found</H4>
														</Col>
													</Row>
												)}
											</div>
										)
									}
								]}
							/>
						</Col>
					</Row>

					<Row className='text-left m-t-10 m-b-5'>
						<Col>
							<AccordianStyled
								content={[
									{
										header: (
											<Row className='align-center'>
												<Col className='col-12'>Session Notes</Col>
											</Row>
										),
										content: (
											<NotesContainer
												data={fetchSessionDetailsApiState.data}
												coaches={fetchCoachesApiState.data}
												isRcb={false}
												auth={true}
											/>
										)
									}
								]}
							/>
						</Col>
					</Row>

					<Row className='text-left m-t-10 m-b-5'>
						<Col>
							<AccordianStyled
								content={[
									{
										header: (
											<Row className='align-center'>
												<Col className='col-12'>RCB Notes</Col>
											</Row>
										),
										content: (
											<NotesContainer
												data={rcbNotes.data}
												coaches={fetchCoachesApiState.data}
												isRcb={true}
												auth={authData?.data?.email === assignedCoach}
											/>
										)
									}
								]}
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
								Notes
							</H4>
						</Col>
						<Col>
							<H6 style={{textAlign: "left"}} className='m-t-0 m-b-2'>
								Notes from this session*
							</H6>
						</Col>
						<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
							<Row>
								<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
									<FormElementContainerFullWidth>
										<FormInputContainer style={{flexBasis: "100%"}}>
											<Controller
												name={"content"}
												control={control}
												rules={{
													required: {value: true, message: "Note is required"},
													validate: text => {
														if (!watchTextArea.content) return true
														return validateText(text, "Note")
													},
													maxLength: {
														value: 20000,
														message: "Field can not exceed 20,000 characters"
													}
												}}
												defaultValue={sessionNote}
												render={props => {
													return (
														<TextAreaVert
															name='content'
															minHeight='200px'
															outline={false}
															//validation={errors}
															value={props.value}
															onChange={event => {
																props.onChange(event.target.value)
																trigger("content")
																autoSaveNotesApiCall(event.target.value)
															}}
															border={true}
															padding={spacing.s3}
															placeholder='Your session note..'
															disabled={codeExpired}
															id='content'
														/>
													)
												}}
											/>
										</FormInputContainer>
									</FormElementContainerFullWidth>
									{errors["content"] && errors["content"].message ? (
										<MessageStyles htmlFor={1} focused={false} validation={errors["content"]}>
											{errors["content"] && errors["content"].message}
										</MessageStyles>
									) : null}
								</Col>
							</Row>
						</Col>
					</Row>

					<Row>
						<Col>
							<H6 style={{textAlign: "left"}} className='m-b-2'>
								Topic discussed*
							</H6>
						</Col>
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
							<FormElementContainer>
								<FormInputContainer style={{flexBasis: "100%"}}>
									<Controller
										name={"issues"}
										control={control}
										rules={{
											required: {
												value: true,
												message: "Please select a topic"
											}
										}}
										render={props => (
											<DropDown
												isFirst={false}
												isLast={true}
												disabled={codeExpired}
												items={issuesDropdown.map(issue => ({label: issue, value: issue}))}
												initialSelectedItem={
													booking.coachTopic
														? !issuesDropdown.includes(booking.coachTopic)
															? {label: issuesDropdown[17], value: issuesDropdown[17]}
															: {label: booking.coachTopic, value: booking.coachTopic}
														: null
												}
												label='Select Item'
												variant='primary'
												onChange={item => props.onChange(item.value)}
												menuStyle={{position: "absolute", zIndex: 2}}
												sort={false}
											/>
										)}
									/>
								</FormInputContainer>
							</FormElementContainer>

							{errors["issues"] && errors["issues"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["issues"]}>
									{errors["issues"] && errors["issues"].message}
								</MessageStyles>
							) : null}
						</Col>

						<Col className='col-6 col-lg-6 col-md-6 col-sm-12' style={{display: enableIssue || "None"}}>
							<FormElementContainer>
								<FormInputContainer style={{flexBasis: "100%"}}>
									<Controller
										name={"otherIssue"}
										control={control}
										rules={{
											required: {
												value: enableIssue,
												message: "Field can not be left empty"
											},
											validate: text => {
												if (!enableIssue) return true
												else return validateText(text, "Others")
											},
											maxLength: {
												value: 500,
												message: "Field can not exceed 500 characters"
											}
										}}
										as={
											<FormTextInputWithBorder
												type='text'
												ref={register({required: true})}
												disabled={!enableIssue}
												id={"otherIssue"}
											/>
										}
									/>
								</FormInputContainer>
							</FormElementContainer>

							{errors["otherIssue"] && errors["otherIssue"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["otherIssue"]}>
									{errors["otherIssue"] && errors["otherIssue"].message}
								</MessageStyles>
							) : null}
						</Col>
					</Row>

					<Row>
						<Col>
							<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
								Recommendation
							</H4>
						</Col>
						<Col>
							<H6 style={{textAlign: "left"}} className='m-t-0 m-b-2'>
								My recommendation for the client*
							</H6>
						</Col>
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
							<FormElementContainer>
								<FormInputContainer style={{flexBasis: "100%"}}>
									<Controller
										name={"recommendation"}
										control={control}
										rules={{
											required: {
												value: true,
												message: "Please select an option"
											}
										}}
										render={props => (
											<DropDown
												isFirst={false}
												isLast={true}
												disabled={codeExpired}
												items={RECOMMENDATIONS.map(rec => ({label: rec, value: rec}))}
												initialSelectedItem={
													booking.recommendation
														? !RECOMMENDATIONS.includes(booking.recommendation)
															? {label: RECOMMENDATIONS[6], value: RECOMMENDATIONS[6]}
															: {
																	label: booking.recommendation,
																	value: booking.recommendation
															  }
														: null
												}
												label='Select recommendation'
												variant='primary'
												onChange={item => props.onChange(item.value)}
												menuStyle={{position: "absolute", zIndex: 2}}
												sort={false}
											/>
										)}
									/>
								</FormInputContainer>
							</FormElementContainer>

							{errors["recommendation"] && errors["recommendation"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["recommendation"]}>
									{errors["recommendation"] && errors["recommendation"].message}
								</MessageStyles>
							) : null}
						</Col>

						<Col className='col-6 col-lg-6 col-md-6 col-sm-12' style={{display: enableRecommend || "None"}}>
							<FormElementContainer>
								<FormInputContainer style={{flexBasis: "100%"}}>
									<Controller
										name={"otherRecommendation"}
										control={control}
										rules={{
											required: {
												value: enableRecommend,
												message: "Field can not be left empty"
											},
											validate: text => {
												if (!enableRecommend) return true
												else return validateText(text, "Others")
											},
											maxLength: {
												value: 500,
												message: "Field can not exceed 500 characters"
											}
										}}
										as={
											<FormTextInputWithBorder
												type='text'
												ref={register({required: true})}
												disabled={!enableRecommend}
												id={"otherRecommendation"}
											/>
										}
									/>
								</FormInputContainer>
							</FormElementContainer>

							{errors["otherRecommendation"] && errors["otherRecommendation"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["otherRecommendation"]}>
									{errors["otherRecommendation"] && errors["otherRecommendation"].message}
								</MessageStyles>
							) : null}
						</Col>
					</Row>

					<Row>
						<Col>
							<H6 style={{textAlign: "left"}} className='m-t-5 m-b-2'>
								Remarks(optional)
							</H6>
						</Col>
						<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
							<Row>
								<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
									<FormElementContainerFullWidth>
										<FormInputContainer style={{flexBasis: "100%"}}>
											<Controller
												rules={{
													required: false,
													validate: text => {
														if (!watchTextArea.remarks) return true
														return validateText(text, "Remarks")
													},
													maxLength: {
														value: 500,
														message: "Field can not exceed 500 characters"
													}
												}}
												name={"remarks"}
												control={control}
												defaultValue={booking.remarks}
												render={props => {
													return (
														<TextArea
															name='remarks'
															minHeight='200px'
															outline={false}
															disabled={codeExpired}
															//validation={errors}
															value={props.value}
															onChange={event => props.onChange(event.target.value)}
															border={true}
															padding={spacing.s3}
															placeholder='Add your remarks here..'
															id='remarks'
														/>
													)
												}}
											/>
										</FormInputContainer>
									</FormElementContainerFullWidth>
									{errors["remarks"] && errors["remarks"].message ? (
										<MessageStyles htmlFor={1} focused={false} validation={errors["remarks"]}>
											{errors["remarks"] && errors["remarks"].message}
										</MessageStyles>
									) : null}
								</Col>
							</Row>
						</Col>
					</Row>

					<Row>
						<Col>
							<H4 style={{textAlign: "left"}} className='m-t-5 m-b-5'>
								Actual Session Details
							</H4>
						</Col>
						<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
							<FormElementContainer error={errors.coachingType?.type === "required"}>
								<FormInputContainer>
									<Controller
										name={"sessionDetails"}
										control={control}
										rules={{
											required: {
												value: true,
												message: "Session Details is required"
											}
										}}
										defaultValue={"coaching-completed"}
										render={props => (
											<>
												<Row>
													<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
														<Radio
															checked={
																watchSession.sessionDetails === "coaching-completed"
															}
															label={"Completed - Coaching"}
															value={"coaching-completed"}
															disabled={codeExpired}
															onChange={e =>
																setValue("sessionDetails", "coaching-completed", {
																	shouldValidate: true
																})
															}
															name={"sessionDetails"}
														/>
													</Col>
													{watchSession.sessionDetails === "coaching-completed" && (
														<Controller
															name={"sessionDuration"}
															control={control}
															rules={{required: "Session time is required"}}
															defaultValue={() => {
																const {completionStatus} = booking
																if (
																	completionStatus === SESSION_DETAILS[0] ||
																	completionStatus === SESSION_DETAILS[1]
																) {
																	setValue("sessionDuration", completionStatus, {
																		shouldValidate: true
																	})
																	return completionStatus
																} else {
																	setValue("sessionDuration", SESSION_DETAILS[0], {
																		shouldValidate: true
																	})
																	return SESSION_DETAILS[0]
																}
															}}
															render={props => (
																<Row style={{paddingLeft: "45px"}}>
																	<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
																		<Radio
																			disabled={codeExpired}
																			checked={
																				watchSession.sessionDuration ===
																				SESSION_DETAILS[0]
																			}
																			label={"30 minute session"}
																			value={SESSION_DETAILS[0]}
																			onChange={e =>
																				setValue(
																					"sessionDuration",
																					SESSION_DETAILS[0],
																					{
																						shouldValidate: true
																					}
																				)
																			}
																			name={"sessionDuration"}
																			width={"250px"}
																		/>
																	</Col>
																	<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
																		<Radio
																			disabled={codeExpired}
																			checked={
																				watchSession.sessionDuration ===
																				SESSION_DETAILS[1]
																			}
																			label={"60 minute session"}
																			value={SESSION_DETAILS[1]}
																			onChange={e =>
																				setValue(
																					"sessionDuration",
																					SESSION_DETAILS[1],
																					{
																						shouldValidate: true
																					}
																				)
																			}
																			name={"sessionDuration"}
																			width={"250px"}
																		/>
																	</Col>
																</Row>
															)}
														/>
													)}
													<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
														<Radio
															checked={
																watchSession.sessionDetails === "therapy-completed"
															}
															disabled={codeExpired}
															label={"Completed - Counselling"}
															value={"therapy-completed"}
															onChange={e =>
																setValue("sessionDetails", "therapy-completed", {
																	shouldValidate: true
																})
															}
															name={"sessionDetails"}
															width={"250px"}
														/>
													</Col>
													{watchSession.sessionDetails === "therapy-completed" && (
														<Controller
															name={"sessionDuration"}
															control={control}
															rules={{required: "Session time is required"}}
															defaultValue={() => {
																const {completionStatus} = booking
																if (
																	completionStatus === SESSION_DETAILS[2] ||
																	completionStatus === SESSION_DETAILS[3]
																) {
																	setValue("sessionDuration", completionStatus, {
																		shouldValidate: true
																	})
																	return completionStatus
																} else {
																	setValue("sessionDuration", SESSION_DETAILS[2], {
																		shouldValidate: true
																	})
																	return SESSION_DETAILS[2]
																}
															}}
															render={props => (
																<Row style={{paddingLeft: "45px"}}>
																	<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
																		<Radio
																			disabled={codeExpired}
																			checked={
																				watchSession.sessionDuration ===
																				SESSION_DETAILS[2]
																			}
																			label={"30 minute session"}
																			value={SESSION_DETAILS[2]}
																			onChange={e =>
																				setValue(
																					"sessionDuration",
																					SESSION_DETAILS[2],
																					{
																						shouldValidate: true
																					}
																				)
																			}
																			name={"sessionDuration"}
																			width={"250px"}
																		/>
																	</Col>
																	<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
																		<Radio
																			disabled={codeExpired}
																			checked={
																				watchSession.sessionDuration ===
																				SESSION_DETAILS[3]
																			}
																			label={"60 minute session"}
																			value={SESSION_DETAILS[3]}
																			onChange={e =>
																				setValue(
																					"sessionDuration",
																					SESSION_DETAILS[3],
																					{
																						shouldValidate: true
																					}
																				)
																			}
																			name={"sessionDuration"}
																			width={"250px"}
																		/>
																	</Col>
																</Row>
															)}
														/>
													)}
													<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
														<Radio
															checked={watchSession.sessionDetails === SESSION_DETAILS[4]}
															label={"No show"}
															value={SESSION_DETAILS[4]}
															onChange={e =>
																setValue("sessionDetails", SESSION_DETAILS[4], {
																	shouldValidate: true
																})
															}
															name={"sessionDetails"}
															width={"250px"}
														/>
													</Col>
												</Row>
											</>
										)}
									/>
								</FormInputContainer>
							</FormElementContainer>

							{errors["sessionDetails"] && errors["sessionDetails"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["sessionDetails"]}>
									{errors["sessionDetails"] && errors["sessionDetails"].message}
								</MessageStyles>
							) : null}
						</Col>
					</Row>

					<Row className='flex-row-reverse'>
						<ButtonContainer>
							<PrimaryButton
								type='button'
								variant={"tertiary"}
								title={"Cancel"}
								onClick={() => setShowModal(true)}
							/>
							<PrimaryButton
								type='submit'
								variant={"primary"}
								title={"Publish"}
								isLoading={loading}
								disabled={codeExpired || booking.coachId !== authData?.data?.id}
							/>
						</ButtonContainer>
					</Row>
				</div>
			</form>
			<ModalStyled
				isOpen={showModal}
				handleClose={handleModalClose}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"350px"}
				scroll={true}
				textAlign='center'
			>
				<ModalButtonContainer>
					<H6 style={{textAlign: "left", color: extended.charcoal.one}} className='m-t-5 m-b-2'>
						Do you want to discard your changes? <br />
						Your inputs will be lost if you do.
					</H6>
					<HeaderTableContent className='m-t-10'>
						<CustomButton title='Cancel' size='medium' onClick={handleModalClose} variant='secondary' />
						<CustomButton title='Confirm' onClick={handleProceed} size='medium' variant='primary' />
					</HeaderTableContent>
				</ModalButtonContainer>
			</ModalStyled>
		</ContainerFluid>
	)
}

export default CompleteSession
